import type { Answer, Section, Verwijzingen } from '@eformulieren/model'
import { fields, isVisibilityRule, type VisibilityRule } from '@eformulieren/model'
import { answerOfReference, conditionHolds } from './conditions.js'
import { type State, valuesOf } from './state.js'

/**
 * Zichtbaarheidscondities en verborgen velden (ontwerp 5.6, R-33, R-34, R-39).
 *
 * De evaluator rekent in twee stappen: eerst de zichtbaarheid van alle secties en
 * velden, daarna de controles, alleen over wat zichtbaar is. Dit is de eerste
 * stap. Ze is een pure functie over de stand: geen I/O, dezelfde invoer geeft
 * altijd dezelfde uitkomst (R-01e), en de stand wordt niet aangeraakt, want een
 * antwoord in een verborgen sectie blijft bewaard (R-39).
 *
 * Zichtbaarheid op grond van autorisatie of anonimisering hoort hier niet: die is
 * klasse B en zit in de projectiestap op de server (R-38, R-01d). Wat hier wordt
 * gerekend is klasse A en mag dus ook in de browser lopen (R-01b).
 *
 * **Drie keuzes die de documenten niet maken.**
 *
 * 1. *Meer dan één conditie op hetzelfde doel geldt als en, niet als of.* B11
 *    beschrijft één conditie per doel, en de definitie sluit een tweede niet uit.
 *    En is de kant die niets zichtbaar maakt wat zonder de tweede conditie
 *    verborgen zou zijn; of zou een conditie kunnen laten overrulen door een
 *    conditie die iemand er later bij zet.
 * 2. *Een conditie op een verborgen bronveld vuurt niet.* Kan de invuller het
 *    bronveld niet zien, dan hoort een oud antwoord erin geen sectie tevoorschijn
 *    te halen. Dit is precies de doorwerking die 5.6 bedoelt.
 * 3. *De berekening levert de ruimste stabiele uitkomst.* Ze begint met alles
 *    zichtbaar en haalt eraf wat een conditie niet haalt. Een kringverwijzing
 *    loopt daarmee niet vast: klopt de kring met de antwoorden, dan blijft hij
 *    staan; klopt één schakel niet, dan valt de hele kring weg. Ruim is hier de
 *    veilige kant, want een verborgen veld valideert niet (R-34) en gaat niet
 *    naar het dossier (R-39); te veel verbergen laat dus stil inhoud vallen.
 *    Kringen zelf blokkeren het publiceren (D-21, `kringen.ts`), dus dit is een
 *    vangnet en geen functie. Dat het een vangnet is en niet overbodig komt door
 *    R-22a: een gepubliceerde pod wordt nooit bijgewerkt, dus een definitie van
 *    vóór die controle kan er nog doorheen komen.
 *
 *    *Tot M5.11a stond hier dat de configurator kringen al bij het vastleggen
 *    weert. Dat was een belofte en geen feit: er bestond nergens een
 *    kringdetectie. Nu wel, in de poort.*
 */

/** Wat er zichtbaar is, als twee verzamelingen ids. */
export interface Visibility {
  readonly secties: ReadonlySet<string>
  readonly velden: ReadonlySet<string>
}

/**
 * Het antwoord waarop een voorwaarde rekent: uit de stand, of bij een verwijzing uit wat het dossier
 * levert (12.31, keuze 2). Een verwijzing staat nooit in de stand, want niemand schrijft haar (R-56). Is
 * ze `onbeschikbaar` of ontbreekt ze, dan is het antwoord leeg (keuze 4, R-63).
 */
function answerFor(fieldId: string, state: State, verwijzingen: Verwijzingen | undefined): Answer | undefined {
  const reference = verwijzingen?.[fieldId]
  if (reference !== undefined) return reference.stand === 'waarde' ? answerOfReference(reference.waarde) : undefined
  return state[fieldId]
}

/**
 * Of een zichtbaarheidsregel het doel laat staan, gegeven wat er in de vorige ronde zichtbaar was.
 *
 * `zichtbaarBij` houdt precies zijn oude betekenis: strikte gelijkheid op de waarden in de stand, en een
 * verwijzing doet niet mee (R-22a: een gepubliceerde pod verandert niet van betekenis). `zichtbaarAls`
 * rekent per voorwaarde met `conditionHolds`; een verborgen bron laat de voorwaarde niet kloppen.
 */
function ruleHolds(
  rule: VisibilityRule,
  visibleFields: ReadonlySet<string>,
  state: State,
  verwijzingen: Verwijzingen | undefined,
): boolean {
  if (rule.soort === 'zichtbaarBij') {
    const values = visibleFields.has(rule.bronVeld) ? valuesOf(state[rule.bronVeld]) : []
    return values.some((value) => rule.heeftEenVan.includes(value))
  }
  const holds = (condition: (typeof rule.voorwaarden)[number]): boolean =>
    visibleFields.has(condition.bronVeld) && conditionHolds(condition, answerFor(condition.bronVeld, state, verwijzingen))
  return rule.combinatie === 'alle' ? rule.voorwaarden.every(holds) : rule.voorwaarden.some(holds)
}

/**
 * Bepaalt wat zichtbaar is, doorrekenend tot de uitkomst stabiel is.
 *
 * De berekening begint met alles zichtbaar en haalt er in elke ronde af wat een
 * conditie niet haalt. Ze wordt dus alleen krapper, nooit ruimer, en komt daarom
 * altijd tot stilstand: hoogstens één ronde per doel plus één ronde om vast te
 * stellen dat er niets meer verandert.
 *
 * Elke ronde toetst tegen de uitkomst van de vórige ronde en niet tegen de
 * uitkomst die halverwege dezelfde ronde ontstaat. Zonder dat zou de volgorde van
 * de secties de uitkomst bepalen, en dat is precies wat R-01e uitsluit.
 *
 * **`verwijzingen` is de derde invoer sinds 31 oktober 2026** (12.31): de opgeloste verwijzingen in de
 * vorm die naar de browser gaat (`Verwijzingen`), zodat de pod en de browser met dezelfde waarden rekenen.
 * Elke plek die zichtbaarheid uitrekent, ook het weglaten van verborgen antwoorden bij indienen (R-39),
 * geeft ze mee, en daarom is de invoer verplicht: de typecontrole dwingt elke aanroeper, en dezelfde
 * invoer geeft overal dezelfde uitkomst (ontwerp 5.9). Wie echt geen verwijzingen heeft, geeft `{}`
 * mee en zegt bij de aanroep waarom; een ontbrekende verwijzing is dan leeg, zoals R-63 het wil.
 */
export function determineVisibility(sections: readonly Section[], state: State, verwijzingen: Verwijzingen): Visibility {
  const allSections = sections.map((section) => section.id)
  const allFields = sections.flatMap((section) => fields(section).map(({ veld: field }) => field.id))

  /** Per doel de condities die erop staan, met de sectie waarin ze staan. */
  const conditions = sections.flatMap((section) =>
    section.regels.filter(isVisibilityRule).map((rule) => ({ sectieId: section.id, regel: rule })),
  )

  let visibleSections = new Set(allSections)
  let visibleFields = new Set(allFields)

  // Hoogstens één ronde per doel om iets te verbergen, plus één om vast te stellen
  // dat er niets meer verandert. De lus stopt eerder zodra de ronde niets afhaalt.
  const maximumRounds = conditions.length + 1

  for (let round = 0; round <= maximumRounds; round += 1) {
    const previousSections = visibleSections
    const previousFields = visibleFields
    const hiddenSections = new Set<string>()
    const hiddenFields = new Set<string>()

    for (const { sectieId: sectionId, regel: rule } of conditions) {
      if (ruleHolds(rule, previousFields, state, verwijzingen)) continue
      if (rule.doel === sectionId) hiddenSections.add(rule.doel)
      else hiddenFields.add(rule.doel)
    }

    visibleSections = new Set([...previousSections].filter((id) => !hiddenSections.has(id)))
    // Een veld in een verborgen sectie is verborgen. De sectie is de dragende
    // eenheid (R-01), dus dit is geen extra regel maar dezelfde regel.
    const fieldsOfHiddenSections = new Set(
      sections
        .filter((section) => !visibleSections.has(section.id))
        .flatMap((section) => fields(section).map(({ veld: field }) => field.id)),
    )
    visibleFields = new Set(
      [...previousFields].filter((id) => !hiddenFields.has(id) && !fieldsOfHiddenSections.has(id)),
    )

    if (visibleSections.size === previousSections.size && visibleFields.size === previousFields.size) {
      return { secties: visibleSections, velden: visibleFields }
    }
  }

  return { secties: visibleSections, velden: visibleFields }
}

/** Of dit veld meedoet aan validatie en aan de uitvoer naar het dossier (R-34, R-39). */
export function fieldVisible(visibility: Visibility, fieldId: string): boolean {
  return visibility.velden.has(fieldId)
}

/** Of deze sectie in beeld is. */
export function sectionVisible(visibility: Visibility, sectionId: string): boolean {
  return visibility.secties.has(sectionId)
}

/**
 * Of een veld meedoet aan de uitvoer naar het dossier: het blokdocument voor een volgend blok, het
 * bericht en de stand voor het processysteem, en wat een adviseur van de partijen ziet (R-34, R-39,
 * ontwerp 5.6).
 *
 * Dezelfde `determineVisibility` als de renderer en de evaluator, over de hele definitie en niet over
 * een projectie: de uitvoer is de waarheid van de server, en een conditie op een bronveld dat deze
 * lezer niet ziet geldt daar gewoon (ontwerp 5.9). De verwijzingen zijn die van de hele definitie
 * (`referenceValues`), zodat een voorwaarde op een verwijzing hier net zo uitpakt als in de pod en de
 * browser (eisenboek 12.31). Het predicaat gaat naar de bouwers in
 * `packages/model`, die de engine niet mogen kennen. De stand zelf blijft onaangeroerd: wie het
 * antwoord verbergt en weer zichtbaar maakt, vindt het terug.
 */
export function visibleInOutput(sections: readonly Section[], state: State, verwijzingen: Verwijzingen): (fieldId: string) => boolean {
  const visibility = determineVisibility(sections, state, verwijzingen)
  return (fieldId) => fieldVisible(visibility, fieldId)
}
